import { useRef, useEffect } from 'react'
import { User, Bot, Copy, Check, AlertCircle } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { useProcessorStore, Message } from '../stores/processorStore'
import { useState } from 'react'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className="p-1.5 hover:bg-gray-700 rounded transition-colors text-gray-500 hover:text-gray-300"
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
    </button>
  )
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user'

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`
          w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
          ${isUser ? 'bg-blue-600' : 'bg-gray-700'}
        `}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      <div
        className={`
          flex-1 min-w-0 rounded-lg p-4 group
          ${isUser ? 'bg-blue-600/20 ml-12' : 'bg-gray-800 mr-12'}
        `}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : message.content ? (
          <div className="relative">
            <div className="markdown-content">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
            <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <CopyButton text={message.content} />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500">
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" />
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse delay-100" />
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse delay-200" />
          </div>
        )}
      </div>
    </div>
  )
}

export function ResultDisplay() {
  const { messages, processingError, uploadedFile } = useProcessorStore()
  const bottomRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  if (!uploadedFile && messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
        <Bot className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg font-medium">Upload a document to get started</p>
        <p className="text-sm mt-1">
          Upload PDFs and images for AI analysis
        </p>
      </div>
    )
  }

  if (uploadedFile && messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
        <Bot className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg font-medium">Document uploaded!</p>
        <p className="text-sm mt-1">
          Ask a question about your {uploadedFile.type === 'pdf' ? 'PDF' : 'image'}
        </p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}

      {processingError && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{processingError}</p>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  )
}
